import { z } from "zod";

export const CircleRoleSchema = z.enum(["OWNER", "MEMBER"]);
export const CircleMemberStatusSchema = z.enum(["ACTIVE", "LEFT", "REMOVED"]);
export const CircleInviteStatusSchema = z.enum(["PENDING", "ACCEPTED", "EXPIRED", "REVOKED"]);

export const CreateCircleSchema = z.object({
  name: z.string().trim().min(1).max(80),
});

export const UpdateCircleSchema = z.object({
  name: z.string().trim().min(1).max(80),
});

export const CircleMembershipSummarySchema = z.object({
  memberId: z.string(),
  role: CircleRoleSchema,
  status: CircleMemberStatusSchema,
});

export const CircleResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdByUserId: z.string(),
  maxMembers: z.number().int().positive(),
  activeMemberCount: z.number().int().nonnegative(),
  pendingInviteCount: z.number().int().nonnegative(),
  myMembership: CircleMembershipSummarySchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const CirclesListResponseSchema = z.object({
  circles: z.array(CircleResponseSchema),
});

export const CreateCircleResponseSchema = z.object({
  circle: CircleResponseSchema,
});

export const CircleMemberResponseSchema = z.object({
  id: z.string(),
  circleId: z.string(),
  userId: z.string(),
  email: z.string().email(),
  displayName: z.string(),
  role: CircleRoleSchema,
  status: CircleMemberStatusSchema,
  joinedAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const CircleMembersListResponseSchema = z.object({
  members: z.array(CircleMemberResponseSchema),
});

export const InviteCircleMemberSchema = z.object({
  email: z.string().trim().email(),
  role: CircleRoleSchema.default("MEMBER"),
});

export const CircleInviteResponseSchema = z.object({
  id: z.string(),
  circleId: z.string(),
  email: z.string().email(),
  invitedByUserId: z.string(),
  role: CircleRoleSchema,
  status: CircleInviteStatusSchema,
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
});

export const CreateCircleInviteResponseSchema = z.object({
  result: z.discriminatedUnion("outcome", [
    z.object({
      outcome: z.literal("INVITED"),
      invite: CircleInviteResponseSchema,
    }),
    z.object({
      outcome: z.literal("ADDED"),
      member: CircleMemberResponseSchema,
    }),
  ]),
});

export const CircleInvitesListResponseSchema = z.object({
  invites: z.array(CircleInviteResponseSchema),
});

export const AcceptCircleInviteResponseSchema = z.object({
  circle: CircleResponseSchema,
  member: CircleMemberResponseSchema,
});

export type CircleRole = z.infer<typeof CircleRoleSchema>;
export type CircleMemberStatus = z.infer<typeof CircleMemberStatusSchema>;
export type CircleInviteStatus = z.infer<typeof CircleInviteStatusSchema>;
export type CreateCircleInput = z.infer<typeof CreateCircleSchema>;
export type UpdateCircleInput = z.infer<typeof UpdateCircleSchema>;
export type CircleMembershipSummary = z.infer<typeof CircleMembershipSummarySchema>;
export type CircleResponse = z.infer<typeof CircleResponseSchema>;
export type CirclesListResponse = z.infer<typeof CirclesListResponseSchema>;
export type CreateCircleResponse = z.infer<typeof CreateCircleResponseSchema>;
export type CircleMemberResponse = z.infer<typeof CircleMemberResponseSchema>;
export type CircleMembersListResponse = z.infer<typeof CircleMembersListResponseSchema>;
export type InviteCircleMemberInput = z.infer<typeof InviteCircleMemberSchema>;
export type CircleInviteResponse = z.infer<typeof CircleInviteResponseSchema>;
export type CreateCircleInviteResponse = z.infer<typeof CreateCircleInviteResponseSchema>;
export type InviteCircleMemberResult = CreateCircleInviteResponse["result"];
export type CircleInvitesListResponse = z.infer<typeof CircleInvitesListResponseSchema>;
export type AcceptCircleInviteResponse = z.infer<typeof AcceptCircleInviteResponseSchema>;

export const CircleInviteInfoSchema = z.object({
  circleName: z.string(),
  inviterName: z.string(),
  inviteeEmail: z.string().email(),
  status: CircleInviteStatusSchema,
  expiresAt: z.string().datetime(),
});

export const CircleInviteInfoResponseSchema = z.object({
  invite: CircleInviteInfoSchema,
});

export type CircleInviteInfo = z.infer<typeof CircleInviteInfoSchema>;
export type CircleInviteInfoResponse = z.infer<typeof CircleInviteInfoResponseSchema>;
